import { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

import { ButtonV1, Loader, TooltipLabel } from "components";
import RecipientWrapper from "./RecipientWrapper";

import { dateConvertor, getMaskData, sentenceCase, timeConvertor } from "utils";
import { useFetchData, useToaster } from "hooks";

import {
  emailStatus,
  notificationHeader,
  notificationStatuses,
} from "./constants";
import { getCall_v2 } from "services";
import { error } from "constants";

const NotificationTab = () => {
  const theme = useTheme();
  const triggerToaster = useToaster();
  const { id } = useParams();

  const getRecipients = (recipientDetails) => {
    const { to, cc, bcc } = recipientDetails;
    const toList = to ? to.split(",").map((e) => e.trim()) : [];
    const ccList = cc ? cc.split(",").map((e) => e.trim()) : [];
    const bccList = bcc ? bcc.split(",").map((e) => e.trim()) : [];

    const totalRecipients = toList.length + ccList.length + bccList.length;

    return { totalRecipients, toList, ccList, bccList };
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
      case "success":
        return {
          color: theme.palette.success.dark,
          borderColor: theme.palette.success.dark,
        };
      case "bounce":
      case "dropped":
      case "spamreport":
      case "failed":
        return {
          color: theme.palette.error.errorTextColor,
          borderColor: theme.palette.error.errorTextColor,
        };
      case "deferred":
      case "unsubscribe":
      case "group_unsubscribe":
        return {
          color: theme.palette.warning.dark,
          borderColor: theme.palette.warning.dark,
        };
      case "open":
      case "read":
      case "click":
        return {
          color: theme.palette.info.light,
          borderColor: theme.palette.info.light,
        };
      case "processed":
      case "sent":
      case "pending":
      case "enqueued":
      case "submitted":
      default:
        return {
          color: theme.palette.warning.dark,
          borderColor: theme.palette.warning.dark,
        };
    }
  };

  const {
    data: notificationData,
    refetch: fetchNotificationData,
    isFetching: isNotificationDataLoading,
  } = useFetchData(
    "fetch-notification-data",
    `/oms/notification/order-number/${id}`,
  );

  const handleOpenPDF = (base64) => {
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      const newWindow = window.open(blobUrl);
      if (!newWindow) {
        alert("Popup blocked! Please enable popups for this site.");
      }
    } catch (error) {
      console.error("Error opening PDF:", error);
      alert("Failed to open PDF. Please try again.");
    }
  };

  const getTemplateData = async (eventId) => {
    try {
      const { data } = await getCall_v2(
        `/oms/notification/event-id/${eventId}`,
      );
      if (data) {
        handleOpenPDF(data?.data.pdfData?.base64);
      } else {
        triggerToaster("Error while fetching template data", error);
      }
    } catch (err) {
      triggerToaster(
        err.message ?? "Error while fetching template data",
        error,
      );
    }
  };

  useEffect(() => {
    fetchNotificationData();
  }, []);

  const handleAttachments = async (data) => {
    try {
      const attachments = data?.attachments;
      const eventId = data?.event_id;
      if (attachments.length) {
        // This function will be cleanup once oms api will be deployed
        const attachmentsNames = attachments.join(",");
        const res = await getCall_v2(
          `/joms/api/external/v1/notification/get-attachment-signed-url?eventId=${eventId}&fileNames=${attachmentsNames}`,
        );
        const result = res?.data?.data?.data;
        if (result?.length) {
          result?.forEach((attachment) => {
            window.open(attachment?.signedUrl, "_blank");
          });
        } else {
          triggerToaster("Error while fetching template data", error);
        }
      }
    } catch (err) {
      triggerToaster(
        err.message ?? "Error while fetching template data",
        error,
      );
    }
  };

  return (
    <Box width={"max-content"} minWidth={"100%"} sx={{ mb: 2 }}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        Notification details
      </Typography>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {notificationHeader.map((item) =>
                item === emailStatus ? (
                  <TableCell key={item}>
                    <Box display={"flex"} alignItems={"center"}>
                      {item}
                      <TooltipLabel
                        placement="right"
                        title={notificationStatuses.map((status) => (
                          <Box key={status?.status} display={"flex"} gap={1}>
                            <Typography fontWeight={"bold"}>
                              {sentenceCase(status.status)} :
                            </Typography>
                            <Typography>{status?.meaning}</Typography>
                          </Box>
                        ))}
                        labelChildren={
                          <InfoOutlined
                            style={{
                              width: 16,
                              marginLeft: 6,
                              marginTop: 4,
                            }}
                            color={"primary"}
                          />
                        }
                      />
                    </Box>
                  </TableCell>
                ) : (
                  <TableCell key={item}>{item}</TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isNotificationDataLoading ? (
              <TableRow>
                <TableCell colSpan={notificationHeader.length}>
                  <Loader sx={{ margin: 0 }} />
                </TableCell>
              </TableRow>
            ) : notificationData?.data?.data?.length ? (
              notificationData?.data?.data?.map((row) => {
                const { totalRecipients, toList, ccList, bccList } =
                  getRecipients(row?.recipient_details);
                const whatsappStatus = row?.whatsapp_status?.[0];

                return (
                  <TableRow key={row?.event_id}>
                    <TableCell>
                      <Typography>
                        {dateConvertor(row?.when_created)}
                      </Typography>
                      <Typography>
                        {timeConvertor(row?.when_created)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row?.email_status?.length ? (
                        <RecipientWrapper
                          firstRecipient={
                            <Box display={"flex"} width={"max-content"} gap={1}>
                              <Typography width={"max-content"}>
                                {row?.email_status?.[0]?.email_id}
                                {" : "}
                              </Typography>
                              <Chip
                                variant="outlined"
                                sx={{
                                  ...getStatusColor(
                                    row?.email_status?.[0]?.status,
                                  ),
                                  width: "fit-content",
                                }}
                                size="small"
                                label={`${sentenceCase(
                                  row?.email_status?.[0]?.status,
                                )}`}
                              />
                            </Box>
                          }
                          totalCount={row?.email_status?.length}
                        >
                          {row?.email_status?.map(({ email_id, status }) => (
                            <Box
                              display={"flex"}
                              width={"max-content"}
                              gap={1}
                              justifyContent={"space-between"}
                            >
                              <Typography mt={1}>{email_id} : </Typography>
                              <Chip
                                variant="outlined"
                                sx={{ ...getStatusColor(status) }}
                                size="small"
                                label={`${sentenceCase(status)}`}
                              />
                            </Box>
                          ))}
                        </RecipientWrapper>
                      ) : (
                        <Typography>-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <>
                        {" "}
                        {whatsappStatus?.receiver &&
                        row?.channels?.includes("WHATSAPP") ? (
                          <>
                            {getMaskData(whatsappStatus?.receiver, 6)} {" : "}
                            <Chip
                              variant="outlined"
                              sx={{
                                ...getStatusColor(whatsappStatus?.status),
                              }}
                              size="small"
                              label={`${sentenceCase(whatsappStatus?.status)}`}
                            />
                          </>
                        ) : (
                          "-"
                        )}
                      </>
                    </TableCell>
                    <TableCell>{sentenceCase(row?.entity) ?? "-"}</TableCell>
                    <TableCell>
                      {totalRecipients > 0 ? (
                        <RecipientWrapper
                          firstRecipient={toList[0] ?? ccList[0] ?? bccList[0]}
                          totalCount={totalRecipients}
                        >
                          {!!row?.recipient_details?.to && (
                            <Typography>
                              <b>to:</b>
                              {toList[0]},
                            </Typography>
                          )}
                          {!!row?.recipient_details?.cc && (
                            <Typography>
                              <b>cc:</b> {ccList[0]},
                              {ccList.slice(1).map((element) => (
                                <Typography key={element}>
                                  {element},
                                </Typography>
                              ))}
                            </Typography>
                          )}
                          {!!row?.recipient_details?.bcc && (
                            <Typography>
                              <b>bcc:</b> {bccList[0]},
                              {bccList.slice(1).map((element) => (
                                <Typography key={element}>
                                  {element},
                                </Typography>
                              ))}
                            </Typography>
                          )}
                        </RecipientWrapper>
                      ) : (
                        <>-</>
                      )}
                    </TableCell>
                    <TableCell>{row?.template_type}</TableCell>
                    <TableCell>
                      <Box>
                        <Box>
                          <ButtonV1
                            size="small"
                            title={"Show template"}
                            onClick={() => getTemplateData(row?.event_id)}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row?.attachments?.length ? (
                        <Box>
                          <Box>
                            <ButtonV1
                              size="small"
                              title={"View attachments"}
                              onClick={() => handleAttachments(row)}
                            />
                          </Box>
                        </Box>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={notificationHeader.length} align="center">
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default NotificationTab;
